// meeting-horario.component.ts

import { Component, OnInit, OnDestroy } from '@angular/core';
import { MeetingApiService } from '../services/meeting-api.service';
import { forkJoin, Subscription } from 'rxjs';
import {
  AvailabilityApi,
  WeekAvailabilityUI,
  TimeSlotUI,
  MeetingType
} from '../models/meeting.model';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';

@Component({
  selector: 'app-meeting-horario',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './meeting-horario.component.html'
})
export class MeetingHorarioComponent implements OnInit, OnDestroy {

  meetingTypes: MeetingType[] = [];
  selectedTypeId!: number;

  currentUserId!: number;
  currentBusinessId!: number;
  currentTimezone = 'America/Lima';

  // INC 19: el backend usa 0=Domingo..6=Sábado; con domingo=7 el POST
  // devolvía 400 y el forkJoin tumbaba todo el guardado
  days = [
    { key: 'monday', label: 'Lunes', dayOfWeek: 1 },
    { key: 'tuesday', label: 'Martes', dayOfWeek: 2 },
    { key: 'wednesday', label: 'Miércoles', dayOfWeek: 3 },
    { key: 'thursday', label: 'Jueves', dayOfWeek: 4 },
    { key: 'friday', label: 'Viernes', dayOfWeek: 5 },
    { key: 'saturday', label: 'Sábado', dayOfWeek: 6 },
    { key: 'sunday', label: 'Domingo', dayOfWeek: 0 }
  ];

  week: WeekAvailabilityUI = this.createDefaultWeek();
  
  existingRecords: Map<string, AvailabilityApi[]> = new Map();

  buffer = 15;
  dailyLimit = 3;
  isDirty = false;
  isLoading = false;
  errorMessage = '';

  private userSubscription!: Subscription;

  constructor(
    private api: MeetingApiService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.loadUserData();
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  loadUserData() {
    this.isLoading = true;
    
    const currentUser = this.authService.getCurrentUser();
    
    if (currentUser && currentUser.id) {
      this.setUserData(currentUser);
      this.loadInitialData();
    } else {
      this.userSubscription = this.authService.currentUser$.subscribe({
        next: (user) => {
          if (user && user.id) {
            this.setUserData(user);
            this.loadInitialData();
          } else if (user === null) {
            this.errorMessage = 'No hay usuario autenticado. Por favor, inicia sesion.';
            this.isLoading = false;
          }
        },
        error: (error) => {
          console.error('Error al obtener usuario:', error);
          this.errorMessage = 'Error al cargar datos de usuario.';
          this.isLoading = false;
        }
      });
    }
  }

  setUserData(user: any) {
    this.currentUserId = user.id;
    this.currentBusinessId = user.businessId || 1;
    
    console.log('Usuario cargado:', {
      id: this.currentUserId,
      businessId: this.currentBusinessId
    });
  }

  loadInitialData() {
    this.loadMeetingTypes();
  }

  onChange() {
    this.isDirty = true;
  }

  createDefaultWeek(): WeekAvailabilityUI {
    return {
      monday: { enabled: true, slots: [{ start: '09:00', end: '18:00' }] },
      tuesday: { enabled: true, slots: [{ start: '09:00', end: '18:00' }] },
      wednesday: { enabled: true, slots: [{ start: '09:00', end: '18:00' }] },
      thursday: { enabled: true, slots: [{ start: '09:00', end: '18:00' }] },
      friday: { enabled: true, slots: [{ start: '09:00', end: '15:00' }] },
      saturday: { enabled: false, slots: [] },
      sunday: { enabled: false, slots: [] }
    };
  }

  loadAvailability() {
    this.isLoading = true;

    // INC 19: antes se descartaba la respuesta y siempre se pintaba la semana
    // por defecto, así que lo guardado nunca se reflejaba en pantalla
    this.api.getAvailabilityByUserAndBusiness(this.currentUserId, this.currentBusinessId).subscribe({
      next: (records: AvailabilityApi[]) => {
        this.week = records.length > 0
          ? this.mapApiToUI(records)
          : this.createDefaultWeek();
        this.isDirty = false;
        this.isLoading = false;
      },
      error: () => {
        this.week = this.createDefaultWeek();
        this.isLoading = false;
      }
    });
  }

  save() {
    if (!this.currentUserId) {
      this.confirmDialog.alert({ title: 'Error', message: 'Error: Usuario no identificado. Por favor, inicia sesion nuevamente.', variant: 'danger' });
      return;
    }
    
    if (!this.currentBusinessId) {
      this.confirmDialog.alert({ title: 'Error', message: 'Error: No se encontro informacion del negocio.', variant: 'danger' });
      return;
    }
    
    if (!this.validateAllDays()) {
      this.confirmDialog.alert({ title: 'Aviso', message: 'Por favor corrige los horarios antes de guardar', variant: 'warning' });
      return;
    }

    this.isLoading = true;

    this.api.getAvailabilityByUserAndBusiness(this.currentUserId, this.currentBusinessId).subscribe({
      next: (existingRecords: AvailabilityApi[]) => {
        console.log('Registros existentes:', existingRecords);
        
        const newRecords: AvailabilityApi[] = [];
        
        this.days.forEach(day => {
          const dayKey = day.key;
          const dayData = this.week[dayKey];

          if (dayData.enabled && dayData.slots.length > 0) {
            dayData.slots.forEach(slot => {
              const record = this.createAvailabilityRecord(day, slot);
              const existing = this.findExistingRecord(existingRecords, record);
              if (existing && existing.id) {
                record.id = existing.id;
              }
              newRecords.push(record);
            });
          }
        });
        
        const toDelete = this.findRecordsToDelete(existingRecords, newRecords);
        const toUpdate = this.findRecordsToUpdate(existingRecords, newRecords);
        const toCreate = this.findRecordsToCreate(existingRecords, newRecords);
        
        console.log('Operaciones:', {
          eliminar: toDelete.length,
          actualizar: toUpdate.length,
          crear: toCreate.length
        });
        
        const requests: any[] = [];
        
        toDelete.forEach(record => {
          if (record.id) {
            requests.push(this.api.deleteAvailability(record.id));
          }
        });
        
        toUpdate.forEach(record => {
          if (record.id) {
            requests.push(this.api.updateAvailability(record.id, record));
          }
        });
        
        toCreate.forEach(record => {
          requests.push(this.api.saveAvailability(record));
        });
        
        if (requests.length === 0) {
          console.log('No hay cambios para guardar');
          this.confirmDialog.alert({ title: 'Aviso', message: 'No hay cambios para guardar' });
          this.isLoading = false;
          return;
        }
        
        forkJoin(requests).subscribe({
          next: (responses) => {
            console.log('Cambios guardados:', responses);
            this.confirmDialog.alert({ title: 'Éxito', message: 'Disponibilidad guardada correctamente', variant: 'success' });
            this.isDirty = false;
            this.isLoading = false;
            this.loadAvailability();
          },
          error: (err) => {
            console.error('Error al guardar:', err);
            this.confirmDialog.alert({ title: 'Error', message: 'Error al guardar la disponibilidad', variant: 'danger' });
            this.isLoading = false;
          }
        });
      },
      error: (error) => {
        console.error('Error al obtener disponibilidad actual:', error);
        this.saveFromScratch();
      }
    });
  }

  private saveFromScratch() {
    console.log('Guardando desde cero...');
    
    const requests: any[] = [];

    this.days.forEach(day => {
      const dayKey = day.key;
      const dayData = this.week[dayKey];

      if (dayData.enabled && dayData.slots.length > 0) {
        dayData.slots.forEach(slot => {
          const record = this.createAvailabilityRecord(day, slot);
          requests.push(this.api.saveAvailability(record));
        });
      }
    });

    if (requests.length === 0) {
      this.confirmDialog.alert({ title: 'Éxito', message: 'Disponibilidad guardada correctamente', variant: 'success' });
      this.isLoading = false;
      return;
    }

    forkJoin(requests).subscribe({
      next: (responses) => {
        console.log('Disponibilidad creada:', responses);
        this.confirmDialog.alert({ title: 'Éxito', message: 'Disponibilidad guardada correctamente', variant: 'success' });
        this.isDirty = false;
        this.isLoading = false;
        this.loadAvailability();
      },
      error: (err) => {
        console.error('Error al guardar:', err);
        this.confirmDialog.alert({ title: 'Error', message: 'Error al guardar la disponibilidad', variant: 'danger' });
        this.isLoading = false;
      }
    });
  }

  private findExistingRecord(existingRecords: AvailabilityApi[], newRecord: AvailabilityApi): AvailabilityApi | undefined {
    return existingRecords.find(record => 
      record.dayOfWeek === newRecord.dayOfWeek && 
      record.startTime === newRecord.startTime && 
      record.endTime === newRecord.endTime
    );
  }

  private findRecordsToDelete(existingRecords: AvailabilityApi[], newRecords: AvailabilityApi[]): AvailabilityApi[] {
    return existingRecords.filter(existing => {
      return !newRecords.some(newRecord => 
        newRecord.dayOfWeek === existing.dayOfWeek &&
        newRecord.startTime === existing.startTime &&
        newRecord.endTime === existing.endTime
      );
    });
  }

  private findRecordsToUpdate(existingRecords: AvailabilityApi[], newRecords: AvailabilityApi[]): AvailabilityApi[] {
    return newRecords.filter(newRecord => {
      const existing = existingRecords.find(record => 
        record.dayOfWeek === newRecord.dayOfWeek &&
        record.startTime === newRecord.startTime &&
        record.endTime === newRecord.endTime
      );
      return existing && existing.id && this.hasChanges(existing, newRecord);
    });
  }

  private findRecordsToCreate(existingRecords: AvailabilityApi[], newRecords: AvailabilityApi[]): AvailabilityApi[] {
    return newRecords.filter(newRecord => {
      return !existingRecords.some(existing => 
        existing.dayOfWeek === newRecord.dayOfWeek &&
        existing.startTime === newRecord.startTime &&
        existing.endTime === newRecord.endTime
      );
    });
  }

  private hasChanges(existing: AvailabilityApi, updated: AvailabilityApi): boolean {
    return existing.startTime !== updated.startTime ||
           existing.endTime !== updated.endTime ||
           existing.isWorkingDay !== updated.isWorkingDay ||
           existing.timezone !== updated.timezone;
  }

  createAvailabilityRecord(day: any, slot: TimeSlotUI): AvailabilityApi {
    return {
      idUser: this.currentUserId,
      idBussines: this.currentBusinessId,
      dayOfWeek: day.dayOfWeek,
      startTime: `${slot.start}:00`,
      endTime: `${slot.end}:00`,
      timezone: this.currentTimezone,
      isWorkingDay: true
    };
  }

  dayMap: { [key: number]: string } = {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday'
  };

  mapApiToUI(data: AvailabilityApi[]): WeekAvailabilityUI {
    // Partir de una semana vacía: si hay registros guardados, la semana por
    // defecto no debe contaminar lo que el usuario configuró
    const week: WeekAvailabilityUI = {
      monday: { enabled: false, slots: [] },
      tuesday: { enabled: false, slots: [] },
      wednesday: { enabled: false, slots: [] },
      thursday: { enabled: false, slots: [] },
      friday: { enabled: false, slots: [] },
      saturday: { enabled: false, slots: [] },
      sunday: { enabled: false, slots: [] }
    };

    data.forEach(record => {
      const key = this.dayMap[record.dayOfWeek];
      
      if (week[key] && record.isWorkingDay) {
        week[key].enabled = true;
        
        const newSlot = {
          start: record.startTime.substring(0, 5),
          end: record.endTime.substring(0, 5)
        };
        
        const slotExists = week[key].slots.some(s => 
          s.start === newSlot.start && s.end === newSlot.end
        );
        
        if (!slotExists) {
          week[key].slots.push(newSlot);
        }
      }
    });

    return week;
  }

  toggleDay(day: string) {
    const d = this.week[day];
    d.enabled = !d.enabled;

    if (d.enabled && d.slots.length === 0) {
      this.addSlot(day);
    }

    if (!d.enabled) {
      d.slots = [];
    }

    this.onChange();
  }

  validateDay(day: string): boolean {
    const d = this.week[day];

    for (let slot of d.slots) {
      if (slot.start >= slot.end) {
        this.confirmDialog.alert({ title: 'Aviso', message: `La hora inicio debe ser menor que la hora fin en ${this.getDayLabel(day)}`, variant: 'warning' });
        return false;
      }
      
      const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(slot.start) || !timeRegex.test(slot.end)) {
        this.confirmDialog.alert({ title: 'Aviso', message: `Formato de hora invalido en ${this.getDayLabel(day)}. Use HH:MM`, variant: 'warning' });
        return false;
      }
    }

    return true;
  }

  validateAllDays(): boolean {
    for (let day of this.days) {
      if (this.week[day.key].enabled && !this.validateDay(day.key)) {
        return false;
      }
    }
    return true;
  }

  getDayLabel(dayKey: string): string {
    const day = this.days.find(d => d.key === dayKey);
    return day ? day.label : dayKey;
  }


  loadMeetingTypes() {
    if (!this.currentBusinessId) {
      console.warn('No hay businessId disponible para cargar tipos de reunion');
      return;
    }
    
    this.api.getMeetingTypes(this.currentBusinessId, true).subscribe({
      next: (types: MeetingType[]) => {
        console.log('Tipos de reunion cargados:', types);
        // INC 5: la duración mostrada debe ser la configurada en el tipo
        // (defaultDurationMinutes), no un valor rotativo 30/45/60 por índice
        this.meetingTypes = types.map((type) => ({
          ...type,
          duration: Number(type.defaultDurationMinutes) || Number(type.duration) || 30
        }));

        if (this.meetingTypes.length > 0) {
          this.selectedTypeId = this.meetingTypes[0].id;
          this.loadAvailability();
        } else {
          console.warn('No hay tipos de reunion configurados');
          this.errorMessage = 'No hay tipos de reunion configurados. Contacta al administrador.';
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('Error al cargar tipos de reunion:', error);
        this.errorMessage = 'Error al cargar tipos de reunion';
        this.isLoading = false;
      }
    });
  }

  selectType(type: MeetingType) {
    if (this.selectedTypeId !== type.id) {
      this.selectedTypeId = type.id;
      this.onChange();
    }
  }

  addSlot(day: string) {
    if (this.week[day].slots.length < this.dailyLimit) {
      this.week[day].slots.push({ start: '09:00', end: '18:00' });
      this.onChange();
    } else {
      this.confirmDialog.alert({ title: 'Aviso', message: `Maximo ${this.dailyLimit} horarios por dia`, variant: 'warning' });
    }
  }

  removeSlot(day: string, index: number) {
    this.week[day].slots.splice(index, 1);
    if (this.week[day].slots.length === 0) {
      this.week[day].enabled = false;
    }
    this.onChange();
  }
}